/**
 * AI Scheduler API Client
 * 
 * Handles communication with the AI Scheduler service backend.
 * Implements the service-to-service API calls for:
 * - Tenant discovery
 * - AI schedule proposal creation
 * - Proposal management and approval workflow
 */

import { useServiceAuthStore } from '../stores/serviceAuth.js'

// Cache constraints outside the class so it survives hot module reload
const constraintsCache = new Map()

class AISchedulerApi {
  constructor() {
    this.serviceAuth = null // Initialize as null, will be lazy-loaded
    this.baseUrl = import.meta.env.DEV 
      ? '/api'  // Use Vite proxy in dev (proxies to https://admin.dgsolutions.se/api)
      : '/api'  // Keep as-is for prod (adjust if needed)
  }
  
  // Lazy-load the service auth store when needed
  getServiceAuth() {
    if (!this.serviceAuth) {
      this.serviceAuth = useServiceAuthStore()
    }
    return this.serviceAuth
  }

  // Generic API request with service authentication
  async apiRequest(endpoint, options = {}) {
    try {
      const response = await this.getServiceAuth().apiRequest(endpoint, options)
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        throw new Error(errorData.detail || errorData.message || `API Error: ${response.status}`)
      }
      
      return await response.json()
    } catch (error) {
      console.error(`AI Scheduler API request failed: ${endpoint}`, error)
      throw error
    }
  }

  // ============================================
  // TENANT DISCOVERY API
  // ============================================

  /**
   * Get list of tenants available for AI scheduling
   * Requires 'tenant:read' scope
   */
  async getTenants(filters = {}) {
    const serviceAuth = this.getServiceAuth()
    if (!serviceAuth.isAuthenticated || !serviceAuth.hasScope('tenant:read')) {
      throw new Error('Authentication required or missing scope: tenant:read')
    }

    const params = new URLSearchParams()
    
    // Add common filters
    if (filters.allowed_for_ai !== undefined) {
      params.append('allowed_for_ai', filters.allowed_for_ai)
    }
    if (filters.billing_status) {
      params.append('billing_status', filters.billing_status)
    }
    if (filters.search) {
      params.append('search', filters.search)
    }

    const endpoint = `/public/tenants/?${params.toString()}`
    const data = await this.apiRequest(endpoint)
    return data.tenants || []
  }

  /**
   * Get detailed information about a specific tenant
   * Requires 'tenant:read' scope
   */
  async getTenant(tenantId) {
    const serviceAuth = this.getServiceAuth()
    if (!serviceAuth.isAuthenticated || !serviceAuth.hasScope('tenant:read')) {
      throw new Error('Authentication required or missing scope: tenant:read')
    }

    try {
      const data = await this.apiRequest(`/public/tenants/${tenantId}/`)
      return data.tenant
    } catch (error) {
      // Fallback: get tenant from the tenants list
      console.warn('Individual tenant endpoint not available, using tenants list fallback')
      const tenantsList = await this.getTenants()
      const tenant = tenantsList.find(t => t.schema_name === tenantId)
      if (!tenant) {
        throw new Error(`Tenant ${tenantId} not found`)
      }
      return tenant
    }
  }

  // ============================================
  // AI PROPOSAL MANAGEMENT API
  // ============================================

  /**
   * Create a new AI schedule proposal
   * Requires 'schedule:propose' scope
   */
  async createProposal(proposalData) {
    if (!this.getServiceAuth().hasScope('schedule:propose')) {
      throw new Error('Missing required scope: schedule:propose')
    }

    const requiredFields = ['tenant_id', 'schedule_start_date', 'schedule_end_date', 'proposal_data']
    for (const field of requiredFields) {
      if (!proposalData[field]) {
        throw new Error(`Missing required field: ${field}`)
      }
    }

    const data = await this.apiRequest('/public/ai/proposals/', {
      method: 'POST',
      body: JSON.stringify({
        tenant_id: proposalData.tenant_id,
        schedule_start_date: proposalData.schedule_start_date,
        schedule_end_date: proposalData.schedule_end_date,
        confidence_score: proposalData.confidence_score || 0.0,
        generated_by: proposalData.generated_by || 'rule_engine',
        generation_model_version: proposalData.generation_model_version || '',
        proposal_data: proposalData.proposal_data,
        summary: proposalData.summary || ''
      })
    })

    return data.proposal
  }

  /**
   * Get list of AI proposals with filtering
   * Requires 'tenant:read' scope
   */
  async getProposals(filters = {}) {
    if (!this.getServiceAuth().hasScope('tenant:read')) {
      throw new Error('Missing required scope: tenant:read')
    }

    const params = new URLSearchParams()
    
    if (filters.tenant_id) {
      params.append('tenant_id', filters.tenant_id)
    }
    if (filters.status) {
      params.append('status', filters.status)
    }
    if (filters.start_date) {
      params.append('start_date', filters.start_date)
    }
    if (filters.end_date) {
      params.append('end_date', filters.end_date)
    }
    if (filters.limit) {
      params.append('limit', filters.limit)
    }
    if (filters.offset) {
      params.append('offset', filters.offset)
    }

    const endpoint = `/public/ai/proposals/list/?${params.toString()}`
    const data = await this.apiRequest(endpoint)
    return data.proposals || []
  }

  /**
   * Get a specific proposal by ID
   */
  async getProposal(proposalId) {
    if (!this.getServiceAuth().hasScope('tenant:read')) {
      throw new Error('Missing required scope: tenant:read')
    }

    const data = await this.apiRequest(`/public/ai/proposals/${proposalId}/`)
    return data.proposal
  }

  /**
   * Update proposal status (approve, reject, reviewing)
   * This endpoint needs to be implemented in the backend
   */
  async updateProposalStatus(proposalId, status, reviewNotes = '', modifiedShifts = null) {
    // This endpoint is mentioned in the documentation but may not be implemented yet
    // For now, we'll simulate the API structure
    
    const validStatuses = ['pending', 'reviewing', 'approved', 'rejected', 'committed']
    if (!validStatuses.includes(status)) {
      throw new Error(`Invalid status. Must be one of: ${validStatuses.join(', ')}`)
    }

    try {
      const data = await this.apiRequest(`/public/ai/proposals/${proposalId}/`, {
        method: 'PATCH',
        body: JSON.stringify({
          status: status,
          review_notes: reviewNotes,
          reviewed_by: 'AI Scheduler Frontend', // This would be the actual reviewer
          modified_shifts: modifiedShifts
        })
      })
      return data.proposal
    } catch (error) {
      // If endpoint doesn't exist yet, log and simulate response
      console.warn('Proposal status update endpoint not implemented yet:', error.message)
      
      // Return simulated success response
      return {
        id: proposalId,
        status: status,
        review_notes: reviewNotes,
        reviewed_at: new Date().toISOString()
      }
    }
  }

  /**
   * Delete a proposal from the backend
   * Used when rejecting proposals instead of just marking them as rejected
   */
  async deleteProposal(proposalId, reason = '') {
    if (!this.getServiceAuth().hasScope('schedule:propose')) {
      throw new Error('Missing required scope: schedule:propose')
    }

    try {
      // Send DELETE request with optional reason
      const data = await this.apiRequest(`/public/ai/proposals/${proposalId}/`, {
        method: 'DELETE',
        body: reason ? JSON.stringify({ reason: reason }) : undefined
      })
      return data
    } catch (error) {
      // If DELETE endpoint doesn't exist, try updating status to rejected
      console.warn('Proposal delete endpoint not implemented, falling back to status update:', error.message)
      
      // Fallback: mark as rejected
      await this.updateProposalStatus(proposalId, 'rejected', reason)
      
      return {
        id: proposalId,
        deleted: false,
        fallback: 'rejected',
        message: 'Proposal marked as rejected (delete endpoint not available)'
      }
    }
  }

  /**
   * Get proposal analytics and metrics
   */
  async getProposalAnalytics(filters = {}) {
    if (!this.getServiceAuth().hasScope('tenant:read')) {
      throw new Error('Missing required scope: tenant:read')
    }

    const params = new URLSearchParams()
    if (filters.tenant_id) {
      params.append('tenant_id', filters.tenant_id)
    }
    if (filters.start_date) {
      params.append('start_date', filters.start_date)
    }
    if (filters.end_date) {
      params.append('end_date', filters.end_date)
    }

    try {
      const data = await this.apiRequest(`/public/ai/proposals/analytics/?${params.toString()}`)
      return data
    } catch (error) {
      console.warn('Proposal analytics endpoint not implemented yet:', error.message)
      
      // Return mock analytics data
      return {
        total_proposals: 0,
        by_status: {
          pending: 0,
          approved: 0,
          rejected: 0,
          committed: 0
        },
        avg_confidence_score: 0.0,
        acceptance_rate: 0.0
      }
    }
  }

  // ============================================
  // TENANT DATA ACCESS (for AI generation)
  // ============================================

  /**
   * Get tenant employee data for schedule generation
   * This would be implemented as part of the cross-tenant read APIs mentioned in the documentation
   */
  async getTenantEmployees(tenantId, filters = {}) {
    if (!this.getServiceAuth().hasScope('tenant:read')) {
      throw new Error('Missing required scope: tenant:read')
    }

    const params = new URLSearchParams()
    if (filters.active_only) {
      params.append('active_only', filters.active_only)
    }
    if (filters.roles) {
      params.append('roles', filters.roles)
    }

    try {
      const data = await this.apiRequest(`/public/tenants/${tenantId}/employees/?${params.toString()}`)
      return data.employees || []
    } catch (error) {
      console.warn('Tenant employee data endpoint not implemented yet:', error.message)
      return []
    }
  }

  /**
   * Get tenant schedule constraints and preferences
   */
  async getTenantConstraints(tenantId) {
    if (!this.getServiceAuth().hasScope('tenant:read')) {
      throw new Error('Missing required scope: tenant:read')
    }

    try {
      const data = await this.apiRequest(`/public/tenants/${tenantId}/constraints/`)
      return data.constraints || {}
    } catch (error) {
      // If it's a 404, the endpoint isn't implemented yet - use defaults
      if (error.message.includes('404')) {
        console.info(`Tenant constraints endpoint not implemented yet for tenant ${tenantId}, using defaults`)
        return {
          working_hours: { start: '09:00', end: '17:00' },
          max_hours_per_week: 40,
          min_rest_between_shifts: 12,
          overtime_rules: {},
          break_requirements: { hours_threshold: 5.5 },
          swedish_collective_agreement: true
        }
      } else {
        // For other errors, log but still provide fallback
        console.warn('Error fetching tenant constraints:', error.message)
        return {
          working_hours: { start: '09:00', end: '17:00' },
          max_hours_per_week: 40,
          min_rest_between_shifts: 12,
          overtime_rules: {},
          break_requirements: { hours_threshold: 5.5 },
          swedish_collective_agreement: true
        }
      }
    }
  }

  // ============================================
  // SCHEDULE GENERATION HELPERS
  // ============================================

  /**
   * Generate schedule proposal using Claude AI backend
   * ALL calculations happen in backend - this is the ONLY generation method now
   */
  async generateClaudeProposal(tenantId, startDate, endDate, options = {}) {
    // Verify service authentication has the required scope
    if (!this.getServiceAuth().hasScope('schedule:propose')) {
      throw new Error('Service account missing required scope: schedule:propose')
    }

    try {
      // Get tenant constraints to send to backend
      const constraints = await this.getTenantConstraints(tenantId).catch(err => {
        return {}
      })

      // Build constraints from form options
      const customConstraints = options.custom_constraints || {}
      const targetWorkersPerHour = customConstraints.target_workers_per_hour || constraints.target_workers_per_hour || 4
      
      const finalConstraints = {
        // Swedish law defaults
        min_staff: targetWorkersPerHour,  // Set min_staff to match target_workers_per_hour
        max_weekly_hours: 40,
        min_rest_hours: 11,
        max_consecutive_days: 6,
        // Backend constraints
        ...constraints,
        // Frontend custom constraints
        ...customConstraints,
        // Specific overrides from options
        ...(options.constraints || {}),
        // Ensure min_staff matches target_workers_per_hour
        min_staff: targetWorkersPerHour,
        target_workers_per_hour: targetWorkersPerHour
      }

      // Format working hours
      if (customConstraints.working_hours) {
        finalConstraints.operating_hours = `${customConstraints.working_hours.start}-${customConstraints.working_hours.end}`
      }
      
      // Ensure consistent break durations (use lunch_break as the authoritative value)
      if (finalConstraints.lunch_break && !finalConstraints.break_duration) {
        finalConstraints.break_duration = finalConstraints.lunch_break
      } else if (finalConstraints.break_duration && !finalConstraints.lunch_break) {
        finalConstraints.lunch_break = finalConstraints.break_duration
      } else if (finalConstraints.lunch_break && finalConstraints.break_duration && 
                 finalConstraints.lunch_break !== finalConstraints.break_duration) {
        // If both exist but differ, use lunch_break
        finalConstraints.break_duration = finalConstraints.lunch_break
      }
      
      // Format request according to backend expectations
      const requestData = {
        tenant_id: tenantId,
        start_date: startDate,
        end_date: endDate,
        constraints: finalConstraints,
        generation_metadata: {
          requested_by: 'frontend_user',
          requested_from: 'ai_scheduler_dashboard',
          priority: 'normal',
          method: options.method || 'claude',
          optimization_goal: options.optimization_goal,
          respect_preferences: options.respect_preferences !== false,
          avoid_conflicts: options.avoid_conflicts !== false,
          balance_workload: options.balance_workload !== false,
          ...options.metadata
        }
      }

      // Debug: Log the constraints being sent
      console.log('🔍 AI Generation Request:', {
        tenant_id: tenantId,
        start_date: startDate,
        end_date: endDate,
        target_workers_per_hour: finalConstraints.target_workers_per_hour,
        constraints: finalConstraints
      })

      // Make request to the backend Claude AI endpoint
      const response = await this.apiRequest('/public/ai/claude/generate/', {
        method: 'POST',
        body: JSON.stringify(requestData)
      })
      
            // Backend returns: { success, proposal: {...}, ai_insights, service_info }
      if (response.success && response.proposal) {
        // The backend returns a minimal proposal object without proposal_data
        // We need to fetch the full proposal to get the actual schedule shifts
        const proposalId = response.proposal.id
        
        // Cache the constraints used for this generation
        constraintsCache.set(proposalId, finalConstraints)
        
        // Fetch the complete proposal with all shift data
        const fullProposal = await this.getProposal(proposalId)
        
        // Return the complete proposal with warnings and insights from generation
        return {
          ...fullProposal,
          // Add generation-specific data
          warnings: response.warnings || [],
          staffing_status: response.staffing_status,
          claude_insights: response.ai_insights,
          service_info: response.service_info
        }
      } else {
        throw new Error('Invalid response format from backend AI service')
      }
      
    } catch (error) {
      console.error('❌ Backend AI generation failed:', {
        error: error.message,
        tenantId,
        startDate,
        endDate
      })
      
      // Provide specific error messages
      if (error.message.includes('500')) {
        throw new Error('Backend AI service error. Check backend logs for details.')
      } else if (error.message.includes('404')) {
        throw new Error('AI generation endpoint not found. Check backend configuration.')
      } else if (error.message.includes('401')) {
        throw new Error('Authentication failed. Check service account credentials.')
      } else if (error.message.includes('403')) {
        throw new Error('Access denied. Service account may lack required scopes.')
      } else if (error.message.includes('400')) {
        throw new Error(`Invalid request: ${error.message}`)
      } else {
        throw new Error(`AI generation failed: ${error.message}`)
      }
    }
  }

  /**
   * Check Claude AI service status
   * Note: Backend incorrectly requires 'schedule:read' scope but should use 'tenant:read'
   */
  async getClaudeStatus(tenantId = null) {
    const serviceAuth = this.getServiceAuth()
    
    // Check for the tenant:read scope (backend has been fixed to use this)
    if (!serviceAuth.hasScope('tenant:read')) {
      console.warn('Service account missing required scope: tenant:read for Claude status')
      return {
        available: false,
        error: 'Missing scope: tenant:read',
        status: 'unauthorized',
        message: 'Service account lacks "tenant:read" scope required for Claude AI status check. Please update the service account configuration.'
      }
    }

    const endpoint = '/public/ai/claude/status/'
    
    try {
      const data = await this.apiRequest(endpoint)
      return {
        ...data,
        available: data.status === 'ready',
        endpoint_used: endpoint
      }
    } catch (error) {
      console.warn('Claude AI status check failed:', error.message)
      
      // If it's a 403 error, provide specific guidance
      if (error.message.includes('403')) {
        return {
          available: false,
          error: error.message,
          status: 'scope_error',
          message: 'Backend requires "schedule:read" scope for Claude status. This scope needs to be added to the service account or the backend should be updated to use "tenant:read" instead.',
          fix_instructions: [
            'Option 1: Add "schedule:read" scope to service account',
            'Option 2: Update backend claude_status_api to use @require_service_scope("tenant:read")'
          ],
          endpoint_used: endpoint
        }
      }
      
      return {
        available: false,
        error: error.message,
        status: 'error',
        message: error.message.includes('404') 
          ? 'Claude AI service not found' 
          : error.message.includes('500')
          ? 'Claude AI service error'
          : error.message.includes('401') || error.message.includes('403')
          ? 'Authentication or authorization failed - check service account scopes'
          : 'Service unavailable',
        endpoint_used: endpoint
      }
    }
  }

  /**
   * Generate schedule proposal for a tenant
   * ALL GENERATION HAPPENS IN BACKEND - frontend just sends request and receives result
   */
  async generateScheduleProposal(tenantId, startDate, endDate, options = {}) {
    try {
      const method = options.method || 'claude'
      
      // ALL methods now use backend - no frontend calculations
      // This calls the Claude AI backend endpoint which does all the work
      return await this.generateClaudeProposal(tenantId, startDate, endDate, {
        ...options,
        method: method
      })
      
    } catch (error) {
      console.error('Failed to generate schedule proposal:', error)
      throw error
    }
  }

  /**
   * Regenerate proposal with feedback from reviewer
   * Takes the original proposal and feedback, sends to AI to create improved version
   */
  async regenerateProposalWithFeedback(proposalId, feedback, options = {}) {
    if (!this.getServiceAuth().hasScope('schedule:propose')) {
      throw new Error('Service account missing required scope: schedule:propose')
    }

    try {
      // Get the original proposal to extract metadata
      const originalProposal = await this.getProposal(proposalId)
      
      // Debug: Log what fields the original proposal actually has
      console.log('📋 Original Proposal Fields:', {
        id: originalProposal.id,
        has_constraints: !!originalProposal.constraints,
        has_proposal_data: !!originalProposal.proposal_data,
        proposal_data_keys: originalProposal.proposal_data ? Object.keys(originalProposal.proposal_data) : [],
        all_keys: Object.keys(originalProposal),
        has_cached_constraints: constraintsCache.has(proposalId)
      })
      
      // Try to get constraints from multiple possible locations
      // Priority: cached constraints > proposal.constraints > proposal_data.constraints
      let constraints = constraintsCache.get(proposalId) ||
                       originalProposal.constraints || 
                       originalProposal.proposal_data?.constraints || 
                       originalProposal.proposal_data?.generation_params?.constraints
      
      // If still no constraints, fetch tenant defaults and use those
      if (!constraints || Object.keys(constraints).length === 0) {
        console.warn('⚠️ No constraints found for original proposal, fetching tenant defaults')
        constraints = await this.getTenantConstraints(originalProposal.tenant_id).catch(() => ({}))
      }
      
      // Build regeneration request with feedback
      const requestData = {
        original_proposal_id: proposalId,
        tenant_id: originalProposal.tenant_id,
        start_date: originalProposal.schedule_start_date,
        end_date: originalProposal.schedule_end_date,
        feedback: feedback,
        constraints: constraints,
        generation_metadata: {
          requested_by: 'frontend_user',
          requested_from: 'ai_scheduler_dashboard',
          priority: 'high',
          method: options.method || 'claude',
          regeneration: true,
          original_proposal_id: proposalId,
          ...options.metadata
        }
      }

      // Debug: Log regeneration request
      console.log('🔄 AI Regeneration Request:', {
        original_proposal_id: proposalId,
        tenant_id: originalProposal.tenant_id,
        feedback: feedback,
        constraints: constraints,
        target_workers_per_hour: constraints?.target_workers_per_hour,
        min_staff: constraints?.min_staff,
        constraint_keys: Object.keys(constraints)
      })

      // Make request to regenerate endpoint
      const response = await this.apiRequest('/public/ai/claude/regenerate/', {
        method: 'POST',
        body: JSON.stringify(requestData)
      })
      
      if (response.success && response.proposal) {
        // Fetch the complete regenerated proposal
        const fullProposal = await this.getProposal(response.proposal.id)
        
        // Cache the constraints for the regenerated proposal too
        constraintsCache.set(response.proposal.id, constraints)
        
        return {
          ...fullProposal,
          warnings: response.warnings || [],
          staffing_status: response.staffing_status,
          claude_insights: response.ai_insights,
          service_info: response.service_info,
          regenerated_from: proposalId,
          applied_feedback: feedback
        }
      } else {
        throw new Error('Invalid response format from regeneration endpoint')
      }
    } catch (error) {
      console.error('Failed to regenerate proposal with feedback:', error)
      throw error
    }
  }

  // ============================================
  // UTILITY METHODS
  // ============================================

  /**
   * Test service authentication and endpoints
   * Useful for debugging API issues
   */
  async testConnection() {
    
    try {
      // Test 1: Check service authentication
      const serviceAuth = this.getServiceAuth()
      
      if (!serviceAuth.isAuthenticated) {
        await serviceAuth.initialize()
      }
      
      // Test 2: Check user authentication  
      const { useAuthStore } = await import('../stores/auth.js')
      const authStore = useAuthStore()
      
      const tenants = await this.getTenants({ allowed_for_ai: true })
      
      // Test 4: Try Claude status with both auth methods
      const claudeStatus = await this.getClaudeStatus()
      
      // Test 5: Try constraints endpoint for first tenant
      if (tenants.length > 0) {
        const constraints = await this.getTenantConstraints(tenants[0].schema_name)
      }
      
      // Test 6: Test direct backend Claude endpoints
      try {
        const directResponse = await fetch('/api/public/ai/claude/status/', {
          headers: {
            'Authorization': `Bearer ${serviceAuth.token}`,
            'Content-Type': 'application/json'
          }
        })
                
        if (directResponse.ok) {
          const directData = await directResponse.json()
        } else {
          const errorData = await directResponse.text()
        }
      } catch (directError) {
        console.error('🔗 Direct Claude test failed:', directError)
      }
      
      return {
        success: true,
        serviceAuthentication: 'OK',
        userAuthentication: authStore.isAuthenticated ? 'OK' : 'Not authenticated',
        tenants: tenants.length,
        claudeStatus: claudeStatus
      }
      
    } catch (error) {
      console.error('❌ Connection test failed:', error)
      return {
        success: false,
        error: error.message
      }
    }
  }

  /**
   * Check service authentication status
   */
  isAuthenticated() {
    return this.getServiceAuth().isAuthenticated
  }

  /**
   * Check if service has required scope
   */
  hasScope(scope) {
    return this.getServiceAuth().hasScope(scope)
  }

  /**
   * Get current service scopes
   */
  getScopes() {
    return this.getServiceAuth().scopes
  }

  // ============================================
  // ML MODEL TRAINING API
  // ============================================

  /**
   * Record that a schedule has been reviewed
   * This makes the review available for ML training
   */
  async recordScheduleReview(proposalId) {
    if (!this.getServiceAuth().hasScope('schedule:review')) {
      console.warn('Missing scope: schedule:review - review recording may fail')
    }

    try {
      return await this.apiRequest('/public/ai/review/record/', {
        method: 'POST',
        body: JSON.stringify({
          proposal_id: proposalId
        })
      })
    } catch (error) {
      console.error('Failed to record schedule review:', error)
      throw error
    }
  }

  /**
   * Train ML model on human-reviewed schedules
   * Requires approved/rejected schedules in database with review notes
   */
  async trainMLModel(epochs = 50) {
    if (!this.getServiceAuth().hasScope('schedule:train')) {
      throw new Error('Missing required scope: schedule:train')
    }

    try {
      return await this.apiRequest('/public/ai/ml/train/', {
        method: 'POST',
        body: JSON.stringify({
          epochs: epochs
        })
      })
    } catch (error) {
      console.error('Failed to train ML model:', error)
      throw error
    }
  }

  /**
   * Initialize service authentication
   */
  async initialize() {
    return await this.getServiceAuth().initialize()
  }
}

// Create and export singleton instance
const aiSchedulerApi = new AISchedulerApi()

// In development, expose for debugging
if (import.meta.env.DEV) {
  window.aiSchedulerApi = aiSchedulerApi
  // Add a specific Claude authentication debug method
  aiSchedulerApi.debugClaudeAuth = async function(tenantId = 'pbcsu') {
    
    const serviceAuth = this.getServiceAuth()
    const { useAuthStore } = await import('../stores/auth.js')
    const authStore = useAuthStore()
    
    // Test Claude-specific endpoints that we know exist
    const endpoints = [
      { url: '/public/ai/claude/status/', method: 'GET', requiredScope: 'schedule:read' },
      { url: '/public/ai/claude/generate/', method: 'POST', requiredScope: 'schedule:propose' }
    ]
    
    for (const endpoint of endpoints) {
      if (!serviceAuth.hasScope(endpoint.requiredScope)) {
        continue
      }
      
      try {
        const requestOptions = {
          method: endpoint.method,
          headers: {
            'Authorization': `Bearer ${serviceAuth.token}`,
            'Content-Type': 'application/json'
          }
        }
        
        // Add test body for POST requests
        if (endpoint.method === 'POST') {
          requestOptions.body = JSON.stringify({
            tenant_id: tenantId,
            start_date: '2025-11-10',
            end_date: '2025-11-16',
            constraints: { min_staff: 2 }
          })
        }
        
        const response = await fetch(`/api${endpoint.url}`, requestOptions)
      } catch (error) {
        // Silently handle errors in debug mode
      }
    }
  }
}

export default aiSchedulerApi

// Export class for testing or multiple instances
export { AISchedulerApi }